import type { TerminalPtyAction } from '@terra/shared-dto-chat-types';
import type { InteractivePtyScreen } from '../../../../types/command-executor.types';
import type { ShellType } from '../../../../types/terminal.types';
import { useBottomPanelStore } from '../../../../store/bottomPanelStore';
import {
  agentSessionTabId,
  useTerminalStore,
} from '../../../../store/terminalStore';

export type InteractiveSessionInput = {
  command?: string;
  executionCommand?: string;
  description?: string;
  cwd?: string;
  shell?: ShellType;
  ptyId?: string;
  ptyAction?: TerminalPtyAction;
  ptyInput?: string;
  waitMs?: number;
};

function requirePtyId({
  input,
  purpose,
}: {
  input: InteractiveSessionInput;
  purpose: string;
}): string {
  if (!input.ptyId) {
    throw new Error(`ptyId is required to ${purpose}`);
  }

  return input.ptyId;
}

async function callPty({
  input,
  chatId,
  sessionId,
}: {
  input: InteractiveSessionInput;
  chatId: string | undefined;
  sessionId: string;
}): Promise<InteractivePtyScreen | null> {
  const api = window.electronAPI.pty;

  switch (input.ptyAction ?? 'open') {
    case 'open':
      return api.open({
        chatId,
        sessionId,
        command: input.executionCommand ?? input.command,
        cwd: input.cwd,
        shell: input.shell,
        waitMs: input.waitMs,
      });
    case 'send':
      return api.write({
        ptyId: requirePtyId({ input, purpose: 'send input' }),
        data: input.ptyInput ?? '',
        waitMs: input.waitMs,
      });
    case 'read':
      return api.read({
        ptyId: requirePtyId({ input, purpose: 'read a session' }),
        waitMs: input.waitMs,
      });
    case 'interrupt':
      return api.interrupt(
        requirePtyId({ input, purpose: 'interrupt a session' }),
      );
    case 'close':
      return api.close(requirePtyId({ input, purpose: 'close a session' }));
  }
}

function revealAgentSessionTab({
  input,
  screen,
  toolCallId,
}: {
  input: InteractiveSessionInput;
  screen: InteractivePtyScreen;
  toolCallId: string;
}): void {
  const terminalStore = useTerminalStore.getState();

  if ((input.ptyAction ?? 'open') === 'open') {
    terminalStore.addAgentSessionTab({
      ptyId: screen.ptyId,
      title: input.description ?? input.command ?? screen.shell,
    });
    useBottomPanelStore.getState().setTab('terminal');
  }

  terminalStore.linkToolCallToTab(toolCallId, agentSessionTabId(screen.ptyId));
}

export async function runInteractiveSessionAction({
  input,
  chatId,
  sessionId,
  toolCallId,
}: {
  input: InteractiveSessionInput;
  chatId: string | undefined;
  sessionId: string;
  toolCallId: string;
}): Promise<InteractivePtyScreen | null> {
  const screen = await callPty({ input, chatId, sessionId });

  if (screen) {
    revealAgentSessionTab({ input, screen, toolCallId });
  }

  return screen;
}
